import { FC, useContext, useEffect } from 'react';
import { Box, TextField, Typography, FormControlLabel, RadioGroup, Radio, FormControl, Select, MenuItem, InputLabel, Button } from '@mui/material';
import { CampaignMode, ICategory, IPurpose, IViewCampaign } from '../../interfaces/campaign';
import { t } from 'i18next';
import { isCampaignCreateMode } from '../../utils/string';
import { CampaignContext } from '../../contexts/CampaignContext';
import IResponse from '../../interfaces/response';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

type ICampaignInfo = {
	mode: CampaignMode;
	editHeader?: boolean;
	isAdvertising?: boolean;
};

const CampaignInfo: FC<ICampaignInfo> = ({ mode, isAdvertising = true, editHeader = false }) => {
	const { id } = useParams();
	const isCreateMode = isCampaignCreateMode(mode);
	const navigate = useNavigate();
	const { contentStatus, description, title, selectedPlatformId, selectedPlatform, selectedContentType, selectedMediaTypeId, selectedTime, selectedCategoryId, fetchedCategories, fetchedCampaign, selectedDate, selectedPages, selectedPackages, setContentStatus, setSelectedCategoryId, setTitle, setDescription, setFetchedPurposes, setFetchedCategories } = useContext(CampaignContext);

	const handleContentStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setContentStatus(parseInt(event.target.value));
	};

	const handleSelectCategory = (e: any) => {
		setSelectedCategoryId(e.target.value);
	};

	const handleChangeTitle = (e: any) => {
		setTitle(e.target.value);
	};

	const handleDescriptionChange = (e: any) => {
		setDescription(e.target.value);
	};

	const filteredCategory = fetchedCategories?.filter(cat => cat.id === fetchedCampaign?.category_id);
	const selectedFetchedCategory = filteredCategory?.[0]?.title;

	useEffect(() => {
		axios<IResponse<IPurpose[]>>({
			url: 'options/purpose',
		}).then(res => {
			if (res.data.success) {
				setFetchedPurposes(res.data.data);
			}
		});
		axios<IResponse<ICategory[]>>({
			url: 'options/category?sort_column=title&sort_direction=asc',
		}).then(res => {
			if (res.data.success) {
				setFetchedCategories(res.data.data);
			}
		});
	}, []);
	const handleEditCampaign = () => () => {
		const formattedDate = selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null;
		const finalParams = {
			id: id,
			is_final: 1,
			campaign_type: isAdvertising ? 'advertising' : 'reportage',
			category_id: selectedCategoryId,
			title: title,
			description: description,
			content_status: contentStatus,
			media_id: selectedPlatformId,
			media_type_id: selectedPlatform === 'sms' ? 'sms' : selectedMediaTypeId,
			content_type: selectedContentType,
			release_time_id: selectedTime,
			release_date: formattedDate,
			entities_price_id: selectedPages?.length > 0 ? selectedPages?.map(page => page.id) : null,
			media_packages_id: selectedPackages?.length > 0 ? selectedPackages?.map(pkg => pkg.id) : null,
		};

		axios<IResponse<IViewCampaign>>({
			method: 'post',
			url: '/campaigns',
			data: finalParams,
		}).then(async res => {
			if (res.data.success) {
				navigate('/campaigns');
			}
		});
	};
	return (
		<Box className={`rounded-b-lg bg-background-paper px-6 pb-8 pt-6`}>
			<Box className="flex w-full gap-x-4 md:ps-36">
				<FormControl required className="grow basis-0">
					{isCreateMode ? (
						<>
							<InputLabel>{t('category_campaign')}</InputLabel>
							<Select value={selectedCategoryId} variant={'outlined'} size="medium" label={t('category')} onChange={handleSelectCategory}>
								{fetchedCategories?.map((cat, i) => (
									<MenuItem key={i} value={cat.id}>
										{cat.title}
									</MenuItem>
								))}
							</Select>
						</>
					) : (
						<TextField value={selectedFetchedCategory} disabled fullWidth variant={'filled'} size="medium" label={t('category')} />
					)}
					{!selectedCategoryId && <Typography className="mt-1 text-red-600">{t('selected_category_is_required')}</Typography>}
				</FormControl>
				<FormControl className="grow basis-0">
					<TextField required value={isCreateMode ? title : fetchedCampaign?.title} disabled={!isCreateMode} size="medium" fullWidth variant={isCreateMode ? 'outlined' : 'filled'} label={t('title_campaign')} onChange={handleChangeTitle} />
					{!title && <Typography className="mt-1 text-red-600">{t('selected_title_is_required')}</Typography>}
				</FormControl>
			</Box>
			<Box className="flex items-center">
				<Typography className="me-5 w-36 text-xl text-text-primary">{t('description')}</Typography>‌
				<Box className="mb-3 w-full">
					<TextField disabled={!isCreateMode} required className="mt-6" fullWidth variant={isCreateMode ? 'outlined' : 'filled'} onChange={handleDescriptionChange} label={t('description_campaign')} multiline rows={4} value={isCreateMode ? description : fetchedCampaign?.description} />
					{!description && <Typography className="mt-1 text-red-600">{t('selected_dec_is_required')}</Typography>}
				</Box>
			</Box>
			<Box className="flex items-center">
				<Typography className="me-24 text-xl text-text-primary">{t('content')}</Typography>
				<RadioGroup value={contentStatus} onChange={handleContentStatusChange} className="flex flex-row items-center gap-x-4">
					<FormControlLabel disabled={!isCreateMode} value={0} control={<Radio />} label={t('already-have-content')} checked={!isCreateMode ? fetchedCampaign?.content_status === 0 : undefined} />
					<FormControlLabel disabled={!isCreateMode} value={1} control={<Radio />} label={t('content-be-created-by-your-team')} checked={!isCreateMode ? fetchedCampaign?.content_status === 1 : undefined} />
				</RadioGroup>
			</Box>
			{editHeader && mode === 'create' && (
				<div className="flex items-center justify-end gap-2">
					<Button size="large" className="mt-4 px-10" onClick={handleEditCampaign()}>
						{t('edit_campaign')}
					</Button>
				</div>
			)}
		</Box>
	);
};
export default CampaignInfo;
